import { route } from "./routes.js";
import { z } from "zod";
//#region src/platform.ts
/**
* `/v1/platform/status`, the one non-admin platform route the admin app calls.
*
* Producer `PlatformStatusController` in core-api, under `src/release/`. The path sits outside
* `/v1/admin`, so this file writes it literally and uses no prefix constant.
*
* The route carries no `@RequireOrgPermission`, so any authenticated caller reads it. Kernel-apps
* R-181 removed the intro animation routes on `/v1/platform/intro-animation` and their schemas.
*
* Three facts the shell's `api/types.ts` gets wrong about `PlatformStatus`, and the controller is
* the fact. `nextVersion`, `scheduledAt`, `completedAt` and `ringNumber` are optional in the shell
* type and are always sent, nullable, by both of the controller's branches. `version` is typed
* non-null in the shell and is `release.dp_status.current_version`, a nullable column, so the
* row branch can send null. And the shell's four-member `upgradeStatus` union is narrower than
* the column, which is an unconstrained `varchar` whose only writer today is its own default.
*
* Direction rule (R-28): the response schema is `z.object`. The zod parse in the client removes
* a member that core-api adds and keeps the rest, so one new column cannot red every page that
* reads the route. The route takes no request body, so this file declares no request schema.
*/
/** `GET /v1/platform/status`. No parameter. */
const PLATFORM_STATUS_PATH = "/v1/platform/status";
/**
* `GET /v1/platform/status`. The data plane's version and its upgrade state. Authenticated, with
* no org permission gate.
*/
const GET_PLATFORM_STATUS_ROUTE = route("GET", PLATFORM_STATUS_PATH);
/**
* The upgrade states the platform uses.
*
* `release.dp_status.upgrade_status` is an unconstrained `varchar` with `DEFAULT 'none' NOT NULL`
* and no writer in the tree, so every row answers `'none'` today. This enum is the vocabulary a
* reader branches on; a value written outside the platform would fail this parse rather than
* render as an unknown state.
*/
const platformUpgradeStatusSchema = z.enum([
	"none",
	"scheduled",
	"in_progress",
	"completed"
]);
/**
* `GET /v1/platform/status` response.
*
* Both of the controller's branches send all six members. The no-row branch sends the version
* compiled into `platform.json` with every other member null and `upgradeStatus: 'none'`; the
* row branch sends the columns, and `current_version`, `pending_upgrade_version`,
* `last_upgrade_date`, `upgrade_ring` and `estimated_upgrade_date` are each nullable.
*/
const getPlatformStatusResponseSchema = z.object({
	/** `release.dp_status.current_version`, or `platform.json`'s version when no row exists. */
	version: z.string().nullable(),
	upgradeStatus: platformUpgradeStatusSchema,
	/** `pending_upgrade_version` — the version this DP is moving to, null when none is pending. */
	nextVersion: z.string().nullable(),
	/** ISO-8601 from `estimated_upgrade_date`. Null when nothing is scheduled. */
	scheduledAt: z.string().nullable(),
	/** ISO-8601 from `last_upgrade_date`. Null when no upgrade has completed. */
	completedAt: z.string().nullable(),
	/** `upgrade_ring` — the ring this DP upgrades on. Null when unset. */
	ringNumber: z.number().int().nullable()
});
/** Every route this file declares, for the package's route census. */
const platformRoutes = Object.freeze([GET_PLATFORM_STATUS_ROUTE]);
//#endregion
export { GET_PLATFORM_STATUS_ROUTE, PLATFORM_STATUS_PATH, getPlatformStatusResponseSchema, platformRoutes, platformUpgradeStatusSchema };

//# sourceMappingURL=platform.js.map